/**
 * The PiP voice session’s shared vocabulary (#1307, arch doc Â§5, grammar Â§15.6).
 *
 * Promoted from `frontend/rokhop/src/voice/types.ts` (#1217) into the core:
 * the state machine, the LiveKit adapter, the widget and both brand screens
 * share one vocabulary without importing each other. RokHop’s originals are
 * re-export shims so its shipped imports and tests hold (the CP-26 trick).
 *
 * #1307 additions over the #1217 shape: mute, duplex text (`sendText` +
 * `DuplexSendError`), live transcription segments, and the minted
 * conversation id on the snapshot (the thread record the transcript lands in).
 */

/**
 * Design grammar §15.2's state table minus Reconnecting (LiveKit reconnects
 * under us; a flicker between two halo colours is worse than holding
 * 'listening' while it does). Mute is a flag on the snapshot, not a state —
 * a muted session is still listening or speaking.
 */
export type VoiceState =
  | 'idle'
  | 'requesting'
  | 'connecting'
  | 'listening'
  | 'speaking'
  | 'ended'
  | 'failed'

/**
 * Why it failed — the axis that decides which sentence the Owner reads.
 * Colour never carries this (§15.3 rule 2); the label does.
 */
export type VoiceFailure =
  | 'mic-denied'
  | 'mic-missing'
  | 'agent-absent'
  | 'unavailable'
  | 'network'

/**
 * C-27 degradation: whether typed turns can reach this session. 'unknown'
 * until the first send attempt; a publish refused by the SFU (session minted
 * before the duplex grant, or the flag is off) resolves it 'unavailable' and
 * the composer falls back to paused — never a silent drop.
 */
export type DuplexAvailability = 'unknown' | 'available' | 'unavailable'

export interface VoiceSnapshot {
  state: VoiceState
  /** Non-null only when `state === 'failed'`. */
  failure: VoiceFailure | null
  /** 0..1 amplitude for the activity ring (§15.1). 0 whenever nothing speaks. */
  level: number
  /** Milliseconds talked. Non-null only when `state === 'ended'`. */
  durationMs: number | null
  /**
   * The connect has run long enough to owe the Owner a sentence (§16.3: a
   * bare wait may run 10s before words appear).
   */
  slow: boolean
  /** #1307: mic muted via the widget control. */
  muted: boolean
  /**
   * #1307: the minted conversation id (the LiveKit room name today) — the
   * thread record this session’s transcript persists into (arch Â§5.1).
   * Set on mint success; survives 'ended' so hydration can still fetch;
   * cleared by reset().
   */
  conversationId: string | null
  /**
   * [#1335] The thread this session extends — the text conversation it was
   * started from (`sourceConversationId`), which the thread read serves with
   * this session’s turns inline once they persist. Null when started bare.
   * Set with the mint request; cleared by reset().
   */
  threadId: string | null
  /** #1307: epoch-ms when the agent first became audible (drives the timer). */
  connectedAt: number | null
  /** #1307 C-27: typed-turn availability for this session. */
  duplex: DuplexAvailability
}

export const IDLE_SNAPSHOT: VoiceSnapshot = {
  state: 'idle',
  failure: null,
  level: 0,
  durationMs: null,
  slow: false,
  muted: false,
  conversationId: null,
  threadId: null,
  connectedAt: null,
  duplex: 'unknown',
}

/** True while the Owner is in a conversation that a click should be able to end. */
export function isLive(state: VoiceState): boolean {
  return state === 'requesting' || state === 'connecting' || state === 'listening' || state === 'speaking'
}

/** One live transcription segment (either speaker), streamed mid-utterance. */
export interface TranscriptSegment {
  /** Stable per-utterance id — successive updates to one utterance reuse it. */
  id: string
  text: string
  /** False while the utterance is still being transcribed/streamed. */
  final: boolean
  speaker: 'user' | 'agent'
}

/** What the token mint returns (moved from rokhop/src/api/voice.ts). */
export interface VoiceGrant {
  token: string
  url: string
  roomName: string
  /**
   * TRF (#1731, phase 3): the thread record this session's turns persist
   * into, when the host names it separately from the LiveKit room. Upstream
   * the room IS the record, so this is optional and the snapshot falls back
   * to `roomName`.
   */
  conversationId?: string
}

/** Why a mint failed, in the only three shapes the screen renders differently. */
export type MintFailure = 'agent-absent' | 'unavailable' | 'network'

export class VoiceMintError extends Error {
  readonly kind: MintFailure
  constructor(kind: MintFailure, message: string) {
    super(message)
    this.name = 'VoiceMintError'
    this.kind = kind
  }
}

/**
 * #1307: why a typed turn could not be delivered. 'not-live' = no session to
 * receive it; 'rejected' = the SFU refused the publish (no data grant —
 * session predates the duplex capability, C-27's degradation case).
 */
export class DuplexSendError extends Error {
  readonly kind: 'not-live' | 'rejected'
  constructor(kind: 'not-live' | 'rejected', message: string) {
    super(message)
    this.name = 'DuplexSendError'
    this.kind = kind
  }
}

/**
 * The room, reduced to what the state machine needs.
 *
 * The point of this interface is that `session.ts` never imports
 * `livekit-client`: the machine is driven in tests by a hand-rolled fake, so
 * the transitions are proven without WebRTC, without jsdom shims, and without
 * a real server. `livekitRoom.ts` is the one file that knows the SDK.
 */
export interface RoomHandle {
  connect(url: string, token: string): Promise<void>
  /** Publish the Owner’s microphone. Permission is already granted by here. */
  publishMic(): Promise<void>
  /** The agent joined and is audible. Fires at most once per handle. */
  onAgentPresent(cb: () => void): void
  /** The agent started (true) or stopped (false) talking. */
  onAgentSpeaking(cb: (speaking: boolean) => void): void
  /** The room went away — remote close, network death, or our own `close()`. */
  onClosed(cb: () => void): void
  /** Current amplitude, 0..1, of whoever is talking. */
  level(): number
  close(): Promise<void>
  /** #1307: mute/unmute the published mic. */
  setMicEnabled(enabled: boolean): Promise<void>
  /**
   * #1307 duplex: publish one typed turn on the `lk.chat` text stream.
   * Rejects when the SFU refuses the publish (no canPublishData grant).
   */
  sendText(text: string): Promise<void>
  /** #1307: live transcription segments (`lk.transcription` text streams). */
  onTranscription(cb: (segment: TranscriptSegment) => void): void
}
